import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Body2, CompleteCheckbox, Dot } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useAssigneeTitle } from "~/components/AssigneePicker/useAssigneeTitle";
import { useSecurity } from "~/components/Hooks";
import { ProjectMembersAssigneePicker } from "~/components/ProjectMembersAssigneePicker";
import { SubtaskDatePicker } from "~/components/Subtasks/SubtaskDatePicker";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { TooltipCss } from "~/components/TooltipCss/TooltipCss";
import AvatarContainer from "~/components/Users/AvatarContainer";
import {
  COMPLETE_SUBTASK_ERROR,
  COMPLETE_SUBTASK_SUCCESS,
  EDIT_SUBTASK_ERROR,
  EDIT_SUBTASK_SUCCESS,
} from "~/store/action_types";
import { completeSubtask, editSubtask } from "~/store/actions/actions.subtasks";
import { getConfigOption } from "~/store/selectors/selector.configOptions";
import { getSubtaskById } from "~/store/selectors/selector.subtasks";
import { getTaskById } from "~/store/selectors/selector.tasks";

export const CalendarSubtaskItem = ({ id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const subtask = useSelector((state) => getSubtaskById(state, id));
  const task = useSelector((state) => getTaskById(state, subtask?.task_id));
  const showTaskNumber = useSelector((state) => getConfigOption(state, `show_task_id`));

  const can = useSecurity();
  const canModify = can(["edit"], subtask);

  const userName = useAssigneeTitle(subtask?.assignee_id, subtask?.fake_assignee_name);

  const handleLink = useCallback(() => {
    navigate(`?modal=Task-${subtask?.task_id}-${subtask?.project_id}`);
  }, [navigate, subtask]);

  const handleChangeAssignee = useCallback(
    (assigneeId) => {
      dispatch(
        editSubtask(subtask?.project_id, subtask?.task_id, id, {
          assignee_id: assigneeId,
        })
      ).then((response) => {
        if (response.type === EDIT_SUBTASK_ERROR) {
          showErrorToast(`${t("Unable to update subtask")}.`);
        }
      });
    },
    [dispatch, id, subtask?.project_id, subtask?.task_id, t]
  );

  const onCompleteSubtask = useCallback(
    (e) => {
      e.stopPropagation();
      if (canModify) {
        dispatch(completeSubtask(id)).then((res) => {
          if (res.type === COMPLETE_SUBTASK_SUCCESS) {
            showSuccessToast(t("Subtask completed."));
          } else if (res.type === COMPLETE_SUBTASK_ERROR) {
            showErrorToast(t("Unable to complete subtask."));
          }
        });
      }
    },
    [canModify, dispatch, id, t]
  );

  const handleChangeDate = useCallback(
    ({ to }) => {
      dispatch(editSubtask(subtask?.project_id, subtask?.task_id, id, { due_on: to })).then((response) => {
        if (response.type === EDIT_SUBTASK_SUCCESS) {
          showSuccessToast(t("Subtask rescheduled."));
        }
      });
    },
    [dispatch, id, subtask?.project_id, subtask?.task_id, t]
  );

  return (
    <div
      className={classNames(
        "tw-w-full tw-flex tw-h-10 tw-items-center tw-cursor-pointer tw-border-b tw-border-solid tw-border-0 tw-border-[var(--color-theme-300)] tw-justify-between task-list-item hover:tw-opacity-100"
      )}
      onClick={handleLink}
      data-testid="calendar-item"
    >
      <CompleteCheckbox
        onClick={onCompleteSubtask}
        className="tw-mr-2 tw-ml-1 tw-shrink-0 tw-z-50"
        data-testid="CompleteCheckbox"
        completed={subtask?.is_completed}
        disabled={!canModify}
      />
      <div className="tw-flex-1 tw-truncate tw-flex tw-items-center">
        <Body2
          weight="medium"
          className="tw-truncate tw-max-w-[340px]"
        >
          {subtask?.name}
        </Body2>
        <div className="tw-px-[6px] tw-flex tw-justify-center tw-items-center">
          <Dot
            color="#89898E"
            size={4}
          />
        </div>
        <Body2
          color="tertiary"
          weight="medium"
          className="tw-truncate tw-max-w-[340px]"
        >
          {showTaskNumber ? `#${task?.task_number}: ${task?.name}` : `${task?.name}`}
        </Body2>
      </div>
      <SubtaskDatePicker
        dueOn={subtask?.due_on}
        taskDueOn={task?.get("due_on")}
        handleDateChange={handleChangeDate}
        changeMode="atomic"
        userId={subtask?.assignee_id}
        canModify={canModify}
        className="tw-hidden md:tw-flex"
      />
      {canModify ? (
        <ProjectMembersAssigneePicker
          assigneeId={subtask?.assignee_id}
          projectId={subtask?.project_id}
          fakeAssigneeName={subtask?.fake_assignee_name}
          onChange={handleChangeAssignee}
          hiddenFromClient={subtask?.is_hidden_from_clients}
          data-testid="ProjectMembersAssigneePicker"
          className={classNames({
            "show-on-hover": !subtask?.assignee_id,
          })}
        />
      ) : subtask?.assignee_id ? (
        <TooltipCss
          position="left"
          title={userName}
          data-testid="avatar-tooltip-for-clients"
        >
          <AvatarContainer
            id={subtask?.assignee_id}
            className="tw-mx-1"
          />
        </TooltipCss>
      ) : (
        <div className="tw-w-6 tw-ml-1"></div>
      )}
    </div>
  );
};

CalendarSubtaskItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
